/**
 * T-226: Profile → Time zone. Each person's own time zone, so timed emails such as
 * the alert summary arrive at their local time. Saves on change, like the other
 * Profile fields, with the same check mark. Filled from the browser at sign-in
 * when empty (useTimeZonePrefill); a choice made here is never overwritten.
 *
 * The select stays usable while a save is running (keyboard users can keep moving
 * through it), and the latest choice wins: an older save that answers late never
 * overwrites a newer one. If the newest save fails, the field shows the newest save
 * that worked, which is what the server has.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Globe, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { FormField, IconSelect } from '@/components/form';
import { getMyTimeZone, saveMyTimeZone } from '@/lib/alertDigestApi';
import { timeZoneLabel, timeZoneOptions } from '@/lib/alertDigestWords';
import { onTimeZoneSaved } from '@/lib/timeZoneEvents';

export function TimeZoneField() {
  const [zone, setZone] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [readFailed, setReadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const { common, all } = useMemo(() => timeZoneOptions(), []);
  // The stored zone (the newest save the server confirmed), the newest save sent, the
  // newest save confirmed, and whether the newest save sent has failed.
  const stored = useRef<string | null>(null);
  const latest = useRef(0);
  const confirmed = useRef(0);
  const latestFailed = useRef(false);

  useEffect(() => {
    let live = true;
    getMyTimeZone()
      .then((r) => {
        if (!live || latest.current !== 0) return;
        // A zone the sign-in fill announced while this read was out stays.
        const tz = r.time_zone ?? stored.current;
        stored.current = tz;
        setZone(tz);
      })
      .catch(() => { if (live) setReadFailed(true); })
      .finally(() => { if (live) setLoaded(true); });
    // The sign-in fill saved one while this page showed none: show it (never over a choice).
    const off = onTimeZoneSaved((tz) => {
      if (!live || latest.current !== 0 || stored.current) return;
      stored.current = tz;
      setZone(tz);
      setReadFailed(false);
    });
    return () => { live = false; off(); };
  }, []);

  const change = async (next: string) => {
    if (!next || next === zone) return;
    const mine = ++latest.current;
    latestFailed.current = false;
    setZone(next);
    setSaving(true);
    setSaved(false);
    try {
      const r = await saveMyTimeZone(next);
      // Remember the newest save that worked, even when a newer one is still on its way,
      // so a failure of that newer one falls back to what the server really has.
      if (mine > confirmed.current) {
        confirmed.current = mine;
        stored.current = r.time_zone ?? next;
      }
      if (mine === latest.current) {
        setZone(stored.current);
        setSaved(true);
        setReadFailed(false);
      } else if (latestFailed.current && mine === confirmed.current) {
        setZone(stored.current); // the newer choice failed earlier; this is what was saved
      }
    } catch (err) {
      if (mine !== latest.current) return;
      latestFailed.current = true;
      setZone(stored.current);
      toast.error(err instanceof Error ? err.message : 'Could not save your time zone');
    } finally {
      if (mine === latest.current) setSaving(false);
    }
  };

  // A stored zone outside the lists (rare: an older browser's name) still shows.
  const listed = zone && !common.includes(zone) && !all.includes(zone) ? [zone] : [];
  const status = saving
    ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Saving" />
    : saved ? <Check className="h-4 w-4 text-[#8FB560]" aria-label="Saved" /> : null;

  return (
    <FormField id="time_zone" label="Time Zone" status={status}>
      <IconSelect
        id="time_zone"
        icon={<Globe className="h-4 w-4" />}
        value={zone ?? ''}
        disabled={!loaded}
        aria-busy={saving}
        onChange={(e) => void change(e.target.value)}
      >
        {!zone && <option value="" disabled>Choose your time zone</option>}
        {listed.map((z) => <option key={z} value={z}>{timeZoneLabel(z)}</option>)}
        <optgroup label="United States">
          {common.map((z) => <option key={z} value={z}>{timeZoneLabel(z)}</option>)}
        </optgroup>
        {all.length > 0 && (
          <optgroup label="All time zones">
            {all.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
          </optgroup>
        )}
      </IconSelect>
      {loaded && readFailed && !zone && (
        <p className="text-xs text-muted-foreground">Couldn’t load your time zone.</p>
      )}
      {loaded && !readFailed && !zone && (
        <p className="text-xs text-muted-foreground">Not set yet, so timed emails use Pacific Time.</p>
      )}
    </FormField>
  );
}
